import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { OrganizationBranchListDto } from "services/organizationBranches/restModels";
import { useIsSuperUser } from "shared/hooks/useIsSuperUser";
import { formatDate } from "shared/lib/dateTime";
import { formatPhoneNumber } from "shared/lib/phoneNumber";
import { ActionMenu } from "shared/ui/ActionMenu";
import { DataTable, DataTableColumn } from "shared/ui/DataTable";
import { IconButton } from "shared/ui/IconButton";
import { MoreIcon } from "shared/ui/icons";

import { PAYMENT_STATUS_LABELS } from "../constants";
import {
    AccountsCell,
    MutedLine,
    StatusBadge,
    TariffCell,
    TariffTitle,
} from "../styles";

interface BranchesTableProps {
    items: OrganizationBranchListDto[];
    busyBranchId: number | null;
    onOpenAsClient: (branch: OrganizationBranchListDto) => void;
    onCopySignInLink: (branch: OrganizationBranchListDto) => void;
    footer?: React.ReactNode;
}

const STATUS_TONES = {
    paid: "success",
    overdue: "warning",
    expired: "danger",
} as const;

export const BranchesTable = ({
    items,
    busyBranchId,
    onOpenAsClient,
    onCopySignInLink,
    footer,
}: BranchesTableProps) => {
    const history = useHistory();
    const isSuperUser = useIsSuperUser();
    const [menu, setMenu] = useState<{ branch: OrganizationBranchListDto; anchorEl: HTMLElement } | null>(
        null
    );

    const columns: DataTableColumn<OrganizationBranchListDto>[] = [
        {
            key: "id",
            title: "ID",
            role: "indicator",
            render: (branch) => branch.id,
        },
        {
            key: "name",
            title: "Название",
            role: "primary",
            width: 280,
            // Строкой под названием, а не столбцом: партнёр есть у меньшинства заведений.
            render: (branch) => (
                <>
                    <div>{branch.name || "—"}</div>
                    {isSuperUser && branch.partner && <MutedLine>Партнёр: {branch.partner.name}</MutedLine>}
                </>
            ),
        },
        {
            key: "accounts",
            title: "Аккаунты",
            role: "secondary",
            grow: true,
            render: (branch) =>
                branch.accounts.length === 0 ? (
                    "—"
                ) : (
                    <AccountsCell>
                        {branch.accounts.map((account) => (
                            <div key={account.id}>{formatPhoneNumber(account.phoneNumber)}</div>
                        ))}
                    </AccountsCell>
                ),
        },
        {
            key: "tariff",
            title: "Тариф",
            role: "secondary",
            width: 220,
            render: (branch) => (
                <TariffCell>
                    <TariffTitle>
                        {branch.tariff.title}
                        <StatusBadge $tone={STATUS_TONES[branch.tariff.paymentStatus]}>
                            {PAYMENT_STATUS_LABELS[branch.tariff.paymentStatus]}
                        </StatusBadge>
                    </TariffTitle>
                    <MutedLine>Оплата: {formatDate(branch.tariff.nextPaymentDate)}</MutedLine>
                    <MutedLine>
                        Меню до: {formatDate(branch.tariff.menusActiveTillDate)}
                    </MutedLine>
                </TariffCell>
            ),
        },
        {
            key: "createdAt",
            title: "Создана",
            role: "secondary",
            width: 120,
            render: (branch) => formatDate(branch.createdAt),
        },
        {
            key: "actions",
            role: "action",
            width: 56,
            render: (branch) => (
                <IconButton
                    label="Действия"
                    onClick={(event) => setMenu({ branch, anchorEl: event.currentTarget })}
                    disabled={busyBranchId === branch.id}
                >
                    <MoreIcon />
                </IconButton>
            ),
        },
    ];

    return (
        <>
            <DataTable
                columns={columns}
                rows={items}
                rowKey={(branch) => branch.id}
                onRowClick={(branch) => history.push(`/branchesRegister/general/${branch.id}`)}
                footer={footer}
            />

            <ActionMenu
                open={menu !== null}
                onClose={() => setMenu(null)}
                anchorEl={menu?.anchorEl ?? null}
                title={menu?.branch.name}
                items={[
                    {
                        key: "open",
                        label: "Войти под клиентом",
                        onClick: () => {
                            if (menu) onOpenAsClient(menu.branch);
                            setMenu(null);
                        },
                    },
                    {
                        key: "copy",
                        label: "Скопировать ссылку для входа",
                        onClick: () => {
                            if (menu) onCopySignInLink(menu.branch);
                            setMenu(null);
                        },
                    },
                ]}
            />
        </>
    );
};
